import { FormEvent, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { staffApi, type StaffMessage, type StaffTicket } from '../../api/staff';
import { joinTicketRoom, leaveTicketRoom, useStaffSocket } from '../../hooks/useStaffSocket';
import { Alert, Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { onChatEnterKey } from '../../utils/chat-composer';
import { formatProfessionalRange } from '../../config/support-pricing';
import {
  getPriorityBadgeTone,
  getPriorityLabel,
  TICKET_PRIORITY_OPTIONS,
  type TicketPriority,
} from '../../utils/ticket-priority';
import './staff.css';

function formatTime(value: string) {
  return new Date(value).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
}

function isTicketOpen(status?: string) {
  return status !== 'resolved' && status !== 'closed' && status !== 'cancelled';
}

function suggestedBillableHours(startedAt?: string | null): string {
  if (!startedAt) return '0.25';
  const elapsedMinutes = Math.max(1, (Date.now() - new Date(startedAt).getTime()) / 60_000);
  const roundedHours = Math.max(0.25, Math.ceil(elapsedMinutes / 15) * 0.25);
  return roundedHours.toFixed(2);
}

function formatRemaining(expiresAt?: string | null): string {
  if (!expiresAt) return '';
  const minutes = Math.max(0, Math.ceil((new Date(expiresAt).getTime() - Date.now()) / 60_000));
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return hours > 0 ? `${hours} h ${rest} min` : `${rest} min`;
}

function ChatBubble({
  message,
  retrying,
  onRetry,
}: {
  message: StaffMessage;
  retrying: boolean;
  onRetry: (messageId: number) => void;
}) {
  const className =
    message.sender_type === 'agent'
      ? 'chat-bubble chat-bubble--customer'
      : message.sender_type === 'system'
        ? 'chat-bubble chat-bubble--system'
        : 'chat-bubble chat-bubble--agent';

  return (
    <div className={className}>
      {message.content}
      {message.sender_type !== 'system' ? (
        <span className="chat-bubble__time">{formatTime(message.created_at)}</span>
      ) : null}
      {message.sender_type === 'agent' && message.channel === 'whatsapp' ? (
        <span className="chat-bubble__time">
          {message.delivery_status === 'read' ? 'Leído' : message.delivery_status === 'delivered' ? 'Entregado' : message.delivery_status === 'failed' ? 'No entregado' : message.delivery_status === 'pending' ? 'Enviando…' : 'Enviado'}
        </span>
      ) : null}
      {message.delivery_status === 'failed' ? (
        <button type="button" className="chat-bubble__retry" disabled={retrying} onClick={() => onRetry(message.id)}>
          {retrying ? 'Reintentando…' : 'Reintentar'}
        </button>
      ) : null}
    </div>
  );
}

export function StaffChatPage() {
  const { ticketId } = useParams<{ ticketId: string }>();
  const id = Number(ticketId);
  const [ticket, setTicket] = useState<StaffTicket | null>(null);
  const [messages, setMessages] = useState<StaffMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [retryingMessageId, setRetryingMessageId] = useState<number | null>(null);
  const [resolving, setResolving] = useState(false);
  const [showResolve, setShowResolve] = useState(false);
  const [billableHours, setBillableHours] = useState('1');
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [showLinkAccount, setShowLinkAccount] = useState(false);
  const [linkEmailInput, setLinkEmailInput] = useState('');
  const [linking, setLinking] = useState(false);
  const [retryingCharge, setRetryingCharge] = useState(false);
  const [showInstallAddress, setShowInstallAddress] = useState(false);
  const [businessNameInput, setBusinessNameInput] = useState('');
  const [direccionInput, setDireccionInput] = useState('');
  const [cpInput, setCpInput] = useState('');
  const [poblacionInput, setPoblacionInput] = useState('');
  const [provinciaInput, setProvinciaInput] = useState('');
  const [savingAddress, setSavingAddress] = useState(false);
  const [updatingPriority, setUpdatingPriority] = useState(false);
  const [error, setError] = useState('');
  const bodyRef = useRef<HTMLDivElement>(null);
  const lastMessageIdRef = useRef(0);

  const mergeMessages = (prev: StaffMessage[], incoming: StaffMessage[]) => {
    if (incoming.length === 0) return prev;
    const positions = new Map(prev.map((message, index) => [message.id, index]));
    const merged = [...prev];
    for (const msg of incoming) {
      const position = positions.get(msg.id);
      if (position == null) {
        positions.set(msg.id, merged.length);
        merged.push(msg);
      } else {
        merged[position] = { ...merged[position], ...msg };
      }
    }
    return merged;
  };

  const refreshMessages = async (markRead = false) => {
    const messagesRes = await staffApi.getMessages(id);
    setMessages((prev) => {
      const merged = mergeMessages(prev, messagesRes.messages);
      lastMessageIdRef.current = merged.at(-1)?.id ?? 0;
      return merged;
    });
    if (markRead) {
      await staffApi.markAsRead(id);
    }
  };

  useEffect(() => {
    if (!id) return;

    const load = async () => {
      try {
        const [ticketRes, messagesRes] = await Promise.all([
          staffApi.getTicket(id),
          staffApi.getMessages(id),
        ]);
        setTicket(ticketRes.ticket);
        setMessages(messagesRes.messages);
        lastMessageIdRef.current = messagesRes.messages.at(-1)?.id ?? 0;
        await staffApi.markAsRead(id);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'No se pudo cargar el chat');
      } finally {
        setLoading(false);
      }
    };

    load();
    joinTicketRoom(id);

    const interval = setInterval(() => {
      refreshMessages(true).catch(() => undefined);
    }, 3000);

    return () => {
      clearInterval(interval);
      leaveTicketRoom(id);
    };
  }, [id]);

  useStaffSocket({
    onNewMessage: (message) => {
      if (message.ticket_id !== id) return;
      setMessages((prev) => {
        const merged = mergeMessages(prev, [message]);
        lastMessageIdRef.current = merged.at(-1)?.id ?? lastMessageIdRef.current;
        return merged;
      });
      if (message.sender_type === 'customer' && ticket?.source === 'whatsapp') {
        const expiresAt = new Date(new Date(message.created_at).getTime() + 24 * 60 * 60 * 1000).toISOString();
        setTicket((prev) => prev ? {
          ...prev,
          whatsapp_window: {
            open: true,
            last_customer_message_at: message.created_at,
            expires_at: expiresAt,
          },
        } : prev);
      }
      staffApi.markAsRead(id).catch(() => undefined);
    },
    onTicketUpdated: (updated) => {
      if (updated.id !== id) return;
      setTicket((prev) =>
        prev
          ? {
              ...prev,
              ...updated,
              ...(updated.priority ? { priority: updated.priority } : {}),
            }
          : prev
      );
    },
  });

  useEffect(() => {
    bodyRef.current?.scrollTo({ top: bodyRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  async function submitMessage() {
    if (!draft.trim() || sending) return;

    const content = draft.trim();
    setDraft('');
    setSending(true);
    try {
      const result = await staffApi.sendMessage(id, content);
      setMessages((prev) =>
        prev.some((m) => m.id === result.message.id) ? prev : [...prev, result.message]
      );
      if (result.ticketStatus === 'in_progress') {
        setTicket((prev) => (prev ? { ...prev, status: 'in_progress' } : prev));
      }
    } catch (err) {
      setDraft(content);
      setError(err instanceof Error ? err.message : 'No se pudo enviar');
    } finally {
      setSending(false);
    }
  }

  function handleSend(event: FormEvent) {
    event.preventDefault();
    void submitMessage();
  }

  async function retryMessage(messageId: number) {
    setRetryingMessageId(messageId);
    setError('');
    try {
      const result = await staffApi.retryMessage(messageId);
      setMessages((prev) => mergeMessages(prev, [result.message]));
      if (result.ticketStatus === 'in_progress') {
        setTicket((prev) => prev ? { ...prev, status: 'in_progress' } : prev);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo reintentar el mensaje');
      await refreshMessages().catch(() => undefined);
    } finally {
      setRetryingMessageId(null);
    }
  }

  function openInstallAddress() {
    setBusinessNameInput(ticket?.customer_name ?? '');
    setDireccionInput(ticket?.service_address ?? '');
    setCpInput(ticket?.service_postal_code ?? '');
    setPoblacionInput(ticket?.service_city ?? '');
    setProvinciaInput(ticket?.service_province ?? '');
    setShowInstallAddress(true);
  }

  async function handleSaveInstallAddress() {
    setSavingAddress(true);
    setError('');
    try {
      const result = await staffApi.updateInstallationAddress(id, {
        business_name: businessNameInput.trim() || undefined,
        direccion: direccionInput.trim() || undefined,
        cp: cpInput.trim() || undefined,
        poblacion: poblacionInput.trim() || undefined,
        provincia: provinciaInput.trim() || undefined,
      });
      setTicket((prev) =>
        prev
          ? {
              ...prev,
              customer_name: result.business_name ?? prev.customer_name,
              service_address: result.service_address.direccion,
              service_postal_code: result.service_address.cp,
              service_city: result.service_address.poblacion,
              service_province: result.service_address.provincia,
            }
          : prev
      );
      setShowInstallAddress(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudieron guardar los datos');
    } finally {
      setSavingAddress(false);
    }
  }

  async function handlePriorityChange(priority: TicketPriority) {
    setUpdatingPriority(true);
    setError('');
    try {
      await staffApi.updateTicketPriority(id, priority);
      setTicket((prev) => (prev ? { ...prev, priority } : prev));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo actualizar la prioridad');
    } finally {
      setUpdatingPriority(false);
    }
  }

  async function handleLinkAccount() {
    const email = linkEmailInput.trim();
    if (!email) {
      setError('Indica el email de la cuenta del cliente');
      return;
    }

    setLinking(true);
    setError('');
    try {
      const result = await staffApi.linkAccount(id, email);
      setTicket((prev) =>
        prev
          ? {
              ...prev,
              user_id: result.linkedAccount.userId,
              customer_email: result.linkedAccount.email,
              linkedAccount: {
                userId: result.linkedAccount.userId,
                hasCardOnFile: result.linkedAccount.hasCardOnFile,
              },
            }
          : prev
      );
      setLinkEmailInput('');
      setShowLinkAccount(false);
      await refreshMessages(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo vincular la cuenta');
    } finally {
      setLinking(false);
    }
  }

  async function handleResolve() {
    const hours = Number(billableHours.replace(',', '.'));
    if (!Number.isFinite(hours) || hours < 0 || hours > 24) {
      setError('Indica las horas facturables (entre 0 y 24)');
      return;
    }

    setResolving(true);
    setError('');
    try {
      await staffApi.resolveTicket(id, {
        billable_hours: hours,
        resolution_notes: resolutionNotes.trim() || undefined,
      });
      // El cobro automático ya se ha intentado al resolver; recargamos el ticket
      // para reflejar si quedó pagado, pendiente o fallido.
      const { ticket: updated } = await staffApi.getTicket(id);
      setTicket(updated);
      setShowResolve(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo resolver');
    } finally {
      setResolving(false);
    }
  }

  async function handleRetryCharge() {
    setRetryingCharge(true);
    setError('');
    try {
      const result = await staffApi.retryCharge(id);
      setTicket((prev) =>
        prev
          ? {
              ...prev,
              client_charge_status:
                (result.clientChargeStatus as StaffTicket['client_charge_status']) ??
                prev.client_charge_status,
            }
          : prev
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo reintentar el cobro');
    } finally {
      setRetryingCharge(false);
    }
  }

  if (loading) return <p>Cargando chat…</p>;

  const whatsappWindowClosed = ticket?.source === 'whatsapp' && (
    !ticket.whatsapp_window?.open
    || !ticket.whatsapp_window.expires_at
    || new Date(ticket.whatsapp_window.expires_at).getTime() <= Date.now()
  );

  const clientHourlyRate = Number(
    ticket?.accepted_hourly_rate_eur ?? (ticket?.billing_tier === 'urgent' ? 55 : 35)
  );
  const parsedBillableHours = Number(billableHours.replace(',', '.'));
  const estimatedLaborTotal = Number.isFinite(parsedBillableHours)
    ? parsedBillableHours * clientHourlyRate
    : 0;

  return (
    <div className="staff-chat">
      <div className="staff-chat__header">
        <div>
          <Link to="/staff/mis-tickets" style={{ fontSize: '0.9rem' }}>
            ← Volver
          </Link>
          <h2 style={{ margin: '0.35rem 0 0' }}>
            {ticket?.customer_name || ticket?.subject || `Ticket #${id}`}
          </h2>
          <p style={{ margin: 0, color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>
            {ticket?.source === 'web_panel' ? 'Chat web' : 'WhatsApp'} · {ticket?.customer_email || ticket?.customer_phone}
          </p>
          {ticket ? (
            <div className="staff-chat__priority-row">
              <Badge tone={getPriorityBadgeTone(ticket.priority)}>
                Cola: {getPriorityLabel(ticket.priority)}
              </Badge>
              <Badge tone={ticket.billing_tier === 'urgent' ? 'danger' : 'neutral'}>
                {ticket.billing_tier === 'urgent' ? 'Tarifa urgente · 55 €/h' : 'Tarifa estándar · 35 €/h'}
              </Badge>
              <Badge>{ticket.service_type === 'onsite' ? 'Servicio presencial' : 'Servicio remoto'}</Badge>
              {ticket.linkedAccount ? (
                <Badge tone={ticket.linkedAccount.hasCardOnFile ? 'success' : 'danger'}>
                  {ticket.linkedAccount.hasCardOnFile
                    ? `Vinculado · ${ticket.customer_email}`
                    : `Vinculado sin tarjeta · ${ticket.customer_email}`}
                </Badge>
              ) : (
                <Badge tone="warning">Sin vincular a cuenta</Badge>
              )}
              {ticket.status === 'resolved' &&
              (ticket.client_charge_status === 'pending' || ticket.client_charge_status === 'failed') ? (
                <Badge tone={ticket.client_charge_status === 'failed' ? 'danger' : 'warning'}>
                  {ticket.client_charge_status === 'failed' ? 'Tarjeta rechazada' : 'Sin cobrar'}
                </Badge>
              ) : null}
              {isTicketOpen(ticket.status) ? (
                <label className="staff-chat__priority-control">
                  <span className="sr-only">Cambiar prioridad</span>
                  <select
                    value={ticket.priority}
                    disabled={updatingPriority}
                    onChange={(e) => void handlePriorityChange(e.target.value as TicketPriority)}
                  >
                    {TICKET_PRIORITY_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
            </div>
          ) : null}
        </div>
        {isTicketOpen(ticket?.status) ? (
          <div className="staff-chat__header-actions">
            {!ticket?.linkedAccount ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowLinkAccount((v) => !v);
                  setShowInstallAddress(false);
                  setShowResolve(false);
                }}
              >
                {showLinkAccount ? 'Cancelar' : 'Vincular cliente'}
              </Button>
            ) : null}
            {ticket?.service_type === 'onsite' ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowInstallAddress((v) => !v);
                  if (!showInstallAddress) openInstallAddress();
                  setShowResolve(false);
                  setShowLinkAccount(false);
                }}
              >
                {showInstallAddress ? 'Cancelar' : 'Datos de la visita'}
              </Button>
            ) : null}
            {ticket?.status === 'assigned' || ticket?.status === 'in_progress' ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  const opening = !showResolve;
                  setShowResolve(opening);
                  if (opening) setBillableHours(suggestedBillableHours(ticket.work_started_at));
                  setShowInstallAddress(false);
                  setShowLinkAccount(false);
                }}
              >
                {showResolve ? 'Cancelar' : 'Finalizar y cobrar'}
              </Button>
            ) : null}
          </div>
        ) : null}
        {ticket &&
        ticket.status === 'resolved' &&
        (ticket.client_charge_status === 'pending' || ticket.client_charge_status === 'failed') ? (
          <div className="staff-chat__header-actions">
            <Button
              variant="secondary"
              size="sm"
              loading={retryingCharge}
              disabled={retryingCharge}
              onClick={() => void handleRetryCharge()}
            >
              Reintentar cobro
            </Button>
          </div>
        ) : null}
      </div>

      {error ? <p style={{ color: 'var(--color-danger)', padding: '0 1rem' }}>{error}</p> : null}

      <div className="staff-chat__messages" ref={bodyRef}>
        {messages.map((message) => (
          <ChatBubble
            key={message.id}
            message={message}
            retrying={retryingMessageId === message.id}
            onRetry={(messageId) => void retryMessage(messageId)}
          />
        ))}
      </div>

      {showLinkAccount && isTicketOpen(ticket?.status) ? (
        <div className="staff-resolve-panel">
          <p style={{ margin: 0, fontWeight: 600 }}>Vincular a una cuenta de cliente</p>
          <p className="muted" style={{ margin: 0, fontSize: '0.85rem' }}>
            Busca la cuenta del cliente por email para que el cobro al resolver se cargue
            automáticamente a su tarjeta guardada, sin enviar ningún enlace de pago.
          </p>
          <label>
            Email de la cuenta
            <input
              type="email"
              placeholder="cliente@email.com"
              value={linkEmailInput}
              onChange={(e) => setLinkEmailInput(e.target.value)}
            />
          </label>
          <Button onClick={() => void handleLinkAccount()} disabled={linking || !linkEmailInput.trim()}>
            {linking ? 'Vinculando…' : 'Buscar y vincular'}
          </Button>
        </div>
      ) : null}

      {showInstallAddress && isTicketOpen(ticket?.status) ? (
        <div className="staff-resolve-panel">
          <p style={{ margin: 0, fontWeight: 600 }}>Datos de la visita</p>
          <p className="muted" style={{ margin: 0, fontSize: '0.85rem' }}>
            Recoge estos datos hablando con el cliente por el chat y guárdalos aquí.
          </p>
          <label>
            Nombre del negocio
            <input
              type="text"
              value={businessNameInput}
              onChange={(e) => setBusinessNameInput(e.target.value)}
            />
          </label>
          <label>
            Dirección
            <input
              type="text"
              placeholder="Calle y número"
              value={direccionInput}
              onChange={(e) => setDireccionInput(e.target.value)}
            />
          </label>
          <label>
            Código postal
            <input
              type="text"
              maxLength={5}
              placeholder="28001"
              value={cpInput}
              onChange={(e) => setCpInput(e.target.value)}
            />
          </label>
          <label>
            Población
            <input
              type="text"
              placeholder="Se autocompleta con el CP"
              value={poblacionInput}
              onChange={(e) => setPoblacionInput(e.target.value)}
            />
          </label>
          <label>
            Provincia
            <input
              type="text"
              placeholder="Se autocompleta con el CP"
              value={provinciaInput}
              onChange={(e) => setProvinciaInput(e.target.value)}
            />
          </label>
          <Button onClick={() => void handleSaveInstallAddress()} disabled={savingAddress}>
            {savingAddress ? 'Guardando…' : 'Guardar'}
          </Button>
        </div>
      ) : null}

      {showResolve && isTicketOpen(ticket?.status) ? (
        <div className="staff-resolve-panel">
          {!ticket?.linkedAccount ? (
            <Alert tone="warning">
              Sin cuenta vinculada: el cobro quedará pendiente, no se cargará ninguna tarjeta. Vincula
              al cliente por email antes de resolver si quieres cobro automático.
            </Alert>
          ) : !ticket.linkedAccount.hasCardOnFile ? (
            <Alert tone="warning">
              Esta cuenta no tiene tarjeta guardada: el cobro quedará pendiente. Pide al cliente que
              añada una tarjeta desde su panel antes de resolver.
            </Alert>
          ) : null}
          <label>
            Horas facturables
            <input
              type="number"
              min="0"
              max="24"
              step="0.25"
              value={billableHours}
              onChange={(e) => setBillableHours(e.target.value)}
            />
          </label>
          <p className="muted" style={{ margin: 0, fontSize: '0.85rem' }}>
            Pon 0 para cerrar sin cobrar al cliente (p. ej. pruebas).
          </p>
          <label>
            Notas de resolución (opcional)
            <textarea
              rows={2}
              value={resolutionNotes}
              onChange={(e) => setResolutionNotes(e.target.value)}
            />
          </label>
          <p className="muted" style={{ margin: 0, fontSize: '0.85rem' }}>
            El cliente aceptó {clientHourlyRate} €/h. Mano de obra estimada:{' '}
            <strong>{estimatedLaborTotal.toFixed(2).replace('.', ',')} €</strong>
            {ticket?.service_type === 'onsite' ? ' más el desplazamiento calculado al finalizar.' : '.'}
          </p>
          <p className="muted" style={{ margin: 0, fontSize: '0.85rem' }}>
            Tu remuneración se calculará con la tarifa{' '}
            {ticket?.billing_tier === 'urgent'
              ? `${formatProfessionalRange('urgent')} según tu rol.`
              : `${formatProfessionalRange('standard')} según tu rol.`}
          </p>
          <Button onClick={() => void handleResolve()} disabled={resolving}>
            {resolving ? 'Finalizando…' : 'Confirmar resolución y cobro'}
          </Button>
        </div>
      ) : null}

      {isTicketOpen(ticket?.status) ? (
        <form className="staff-chat__composer" onSubmit={handleSend}>
          {whatsappWindowClosed ? (
            <Alert tone="warning">
              La ventana de WhatsApp está cerrada. El cliente debe volver a escribir para abrir otras 24 horas.
            </Alert>
          ) : ticket?.source === 'whatsapp' ? (
            <span className="muted">Ventana de WhatsApp abierta · quedan {formatRemaining(ticket.whatsapp_window?.expires_at)}</span>
          ) : null}
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => onChatEnterKey(e, () => void submitMessage())}
            placeholder="Escribe tu respuesta… (Enter para enviar)"
            rows={2}
            disabled={sending || whatsappWindowClosed}
          />
          <Button type="submit" disabled={sending || !draft.trim() || whatsappWindowClosed}>
            Enviar
          </Button>
        </form>
      ) : (
        <div className="chat-widget__status">Ticket resuelto</div>
      )}
    </div>
  );
}
